/**
 * useUploadJobDocument Hook
 *
 * Composes the three steps of an S3-presigned upload into a single mutation:
 *   1. request a presigned POST from the backend
 *   2. POST the file bytes to S3 with the returned fields
 *   3. POST metadata back to the backend so a job_file row is created
 *
 * The mutation invalidates the job's documents query (and activity log) on success.
 */

import {
  completeJobDocumentUpload,
  getJobDocumentUploadUrl,
} from '@/domains/jobs/api/jobDocumentsApi';
import { queryKeys } from '@/lib/query-client';
import { resolveUploadMimetype } from '@/shared/lib/fileTypes';
import { uploadToS3Presigned } from '@/shared/lib/presignedUpload';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface UploadJobDocumentInput {
  file: File;
  file_category?: number;
}

export function useUploadJobDocument(jobId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, file_category }: UploadJobDocumentInput) => {
      const contentType = resolveUploadMimetype(file.name, file.type);
      const presigned = await getJobDocumentUploadUrl(jobId, {
        filename: file.name,
        content_type: contentType,
      });

      await uploadToS3Presigned(presigned, file);

      return completeJobDocumentUpload(jobId, {
        name: file.name,
        mime_type: contentType,
        object_key: presigned.object_key,
        file_category,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.documents(String(jobId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.activityLog(String(jobId)) });
    },
    meta: {
      successTitle: 'Document Uploaded',
      successMessage: 'Job document uploaded successfully',
    },
  });
}
